import { useState, useCallback, useRef, useEffect } from 'react';
import { searchArticles } from '../utils/api';

const AUTO_SEARCH_DEBOUNCE_MS = 750;
const SERVER_ERROR_RETRY_DELAY_MS = 1000;

const wait = (delayMs) => new Promise((resolve) => {
  setTimeout(resolve, delayMs);
});

const isServerError = (error) => error.status >= 500 && error.status < 600;

const hasActiveFilters = (filters = {}) => (
  Object.entries(filters).some(([key, value]) => {
    if (key === 'limit' || key === 'offset') {
      return false;
    }

    if (value === null || value === undefined) {
      return false;
    }

    return String(value).trim() !== '';
  })
);

export const useSearch = (initialQuery = '') => {
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [queryTime, setQueryTime] = useState(0);
  const [page, setPage] = useState(1);

  const debounceTimer = useRef(null);
  const abortController = useRef(null);
  const latestRequestId = useRef(0);

  const performSearch = useCallback(async (searchQuery, filters) => {
    const requestId = latestRequestId.current + 1;
    latestRequestId.current = requestId;

    // Cancel previous request if still pending
    if (abortController.current) {
      abortController.current.abort();
      abortController.current = null;
    }

    if (!searchQuery.trim() && !hasActiveFilters(filters)) {
      setResults([]);
      setTotal(0);
      setQueryTime(0);
      setPage(1);
      setLoading(false);
      setError(null);
      return;
    }

    const controller = new AbortController();
    abortController.current = controller;

    setLoading(true);
    setError(null);

    try {
      let data;

      try {
        data = await searchArticles(searchQuery, filters, {
          signal: controller.signal,
        });
      } catch (err) {
        const shouldRetry = (
          isServerError(err)
          && !controller.signal.aborted
          && latestRequestId.current === requestId
        );

        if (!shouldRetry) {
          throw err;
        }

        await wait(SERVER_ERROR_RETRY_DELAY_MS);

        if (controller.signal.aborted || latestRequestId.current !== requestId) {
          return;
        }

        data = await searchArticles(searchQuery, filters, {
          signal: controller.signal,
        });
      }

      if (controller.signal.aborted || latestRequestId.current !== requestId) {
        return;
      }

      setResults(data.results || []);
      setTotal(data.total || 0);
      setQueryTime(data.query_time_ms || 0);
      setPage(Math.floor(filters.offset / filters.limit) + 1);
    } catch (err) {
      if (err.name !== 'AbortError' && latestRequestId.current === requestId) {
        setError(err.message || 'Search failed');
        setResults([]);
        setTotal(0);
      }
    } finally {
      if (latestRequestId.current === requestId) {
        setLoading(false);
        abortController.current = null;
      }
    }
  }, []);

  const debouncedSearch = useCallback((searchQuery, filters, immediate = false) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (immediate) {
      performSearch(searchQuery, filters);
    } else {
      debounceTimer.current = setTimeout(() => {
        performSearch(searchQuery, filters);
      }, AUTO_SEARCH_DEBOUNCE_MS);
    }
  }, [performSearch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      latestRequestId.current += 1;

      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      if (abortController.current) {
        abortController.current.abort();
      }
    };
  }, []);

  return {
    query,
    setQuery,
    results,
    total,
    loading,
    error,
    queryTime,
    page,
    performSearch,
    debouncedSearch,
  };
};
